import React, { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import useMedia from '../../../../common/hooks/useMedia';
import { searchOrigins } from '../../../Navigation/constants';
import { SearchFilterContentTypes } from '../../../Shared/enums';
import { SearchOption } from '../../../Shared/types';
import { useSearchWithAI } from '../../hooks/useSearchWithAI';
import RightArrowButton from '../ProminentSearch/ConversationalSearchElements/RightArrowButton';
import SearchWithAIButton from '../ProminentSearch/ConversationalSearchElements/SearchWithAIButton';
import { saveSearchHistory } from './AutosuggestElements/util';
import {
  NavSearchContext,
  NavSearchContextProvider,
  NavSearchProps,
} from './NavSearchContext';
import CrossAssetNavSearchBox from './NavSearchElements/CrossAssetNavSearchBox';
import NavSearchBox from './NavSearchElements/NavSearchBox';
import { cleanSearchKeywords } from './NavSearchElements/searchUtils';
import ProminentSearchButton from './ProminentSearchElements/ProminentSearchButton';
import ProminentSearchDropdown from './ProminentSearchElements/ProminentSearchDropdown';
import SearchWithAudio from './SearchWithAudioButton';

export interface ProminentSearchProps extends NavSearchProps {
  onDropdownChange: (contentType: string) => void;
  searchTerm: string;
  prominentSearchOptions: SearchOption[];
  contentTypeFromPrevious?: SearchFilterContentTypes;
  showDropDown: boolean;
  showContentToggles?: boolean;
  useNewSearchUI?: boolean;
  alignment?: 'left' | 'center';
  showConversationalSearchCta?: boolean;
}

export default function ProminentSearch(props: ProminentSearchProps) {
  const {
    onDropdownChange,
    onSubmit,
    userHasAnySubscription,
    prominentSearchOptions,
    contentTypeFromPrevious,
    showDropDown,
    showContentToggles = false,
    useNewSearchUI = false,
    useSnapshots = false,
    alignment = 'left',
    showConversationalSearchCta = false,
  } = props;

  const {
    showAiCta,
    getPlaceholder,
    handleConversationalRedirect,
    conversationalSearchEnabled,
    setConversationalSearchEnabled,
    isSearchWithAIEnabledForContentType,
  } = useSearchWithAI({ showConversationalSearchCta });

  const defaultSearchOption =
    prominentSearchOptions?.[0]?.id ?? contentTypeFromPrevious;

  const [contentType, setContentType] = useState(
    defaultSearchOption ?? contentTypeFromPrevious
  );
  const [searchTerm, setSearchTerm] = useState(props.searchTerm);
  // Ensure searchTerm stays up to date when given a new one via props.
  useEffect(() => {
    setSearchTerm(props.searchTerm);
  }, [props.searchTerm]);

  useEffect(() => {
    setContentType(contentTypeFromPrevious);
  }, [contentTypeFromPrevious]);

  // i.e. tailwind md breakpoint https://storywind.netlify.app/?path=/story/design-grids--page#medium-672-1024px
  const isMediumScreen = useMedia('(min-width: 672px)', true);

  const handleDropdownChange = (contentType: string) => {
    if (
      conversationalSearchEnabled &&
      !isSearchWithAIEnabledForContentType(contentType)
    ) {
      setConversationalSearchEnabled(false);
    }
    onDropdownChange(contentType);
    setContentType(contentType as SearchFilterContentTypes);
  };

  const makeEmptySuggestions = () => ({
    keywords: [],
    relatedCollections: [],
    latestCollections: [],
    trendingCollections: [],
    relatedContentTypesAndKeywords: [],
  });

  const [suggestions, setSuggestions] = useState(makeEmptySuggestions());

  const clearSuggestions = useCallback(
    () => setSuggestions(makeEmptySuggestions()),
    []
  );
  const handleSubmit = (
    contentTypeSearched = contentType,
    value = searchTerm,
    searchOriginOverride = ''
  ) => {
    const cleanedSearchKeywords = cleanSearchKeywords(value);
    if (props?.enableSearchHistory) {
      saveSearchHistory(contentTypeSearched, cleanedSearchKeywords);
    }
    const searchOrigin = searchOriginOverride || searchOrigins.SEARCH_BAR;
    if (handleConversationalRedirect(cleanedSearchKeywords, searchOrigin)) {
      return;
    }
    onSubmit(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
  };

  //homepage hero yellow underline for all content types
  const activeUnderline =
    'homepage-hero-product-active underline border-0 thick-3 offset-5 text-decor-color-primary';
  const inactiveUnderline = 'homepage-hero-product text-gray-300 border-0';

  return (
    <NavSearchContextProvider {...props}>
      <NavSearchContext.Consumer>
        {({ setShowSuggestions }) => (
          <div
            data-cy="prominentSearch"
            className={twMerge(
              useNewSearchUI
                ? 'flex flex-col w-full font-inter'
                : 'flex flex-col w-full font-inter text-gray-900',
              showContentToggles ? 'pb-5' : '',
              alignment === 'center' && 'items-center'
            )}
          >
            {showContentToggles && (
              <div className="product m-0 flex text-white text-base font-bold grid-rows-4 pb-5 cursor-pointer">
                {/* Contents of the product div */}
                {prominentSearchOptions.map((link) => (
                  <div
                    role="button"
                    tabIndex={0}
                    key={link.id}
                    className={twMerge(
                      'text-sm pr-2 mobile-md:text-base ppro-sm:pr-4 ppro-sm:text-base md:pr-6 md:text-base lg:pr-6 lg:text-base',
                      contentType === link.id
                        ? activeUnderline
                        : inactiveUnderline
                    )}
                    data-value={link.id}
                    onClick={() => handleDropdownChange(link.id)}
                    onKeyDown={() => handleDropdownChange(link.id)}
                  >
                    {link.label}
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-center gap-4">
              <form
                className={twMerge(
                  useNewSearchUI
                    ? 'rounded-full lg:w-720px md:w-500px flex items-center bg-white border border-solid border-gray-400'
                    : 'border-none rounded-full lg:w-720px md:w-500px pl-5 flex items-center bg-white',
                  showAiCta && 'pl-0 py-1'
                )}
                onSubmit={(event) => {
                  event.preventDefault();
                  setShowSuggestions(false);
                  handleSubmit(contentType, searchTerm);
                }}
              >
                {/* When AI CTA is shown, dropdown moves to the right side */}
                {showDropDown && !showAiCta && (
                  <>
                    <ProminentSearchDropdown
                      onChange={handleDropdownChange}
                      value={contentType}
                      useNewSearchUI={useNewSearchUI}
                      useSnapshots={useSnapshots}
                      clearSuggestions={clearSuggestions}
                    />
                    {!useNewSearchUI && (
                      <div>
                        <div className="w-px h-11 bg-gray-500" />
                      </div>
                    )}
                  </>
                )}
                {showAiCta && <SearchWithAIButton />}
                {/* {only serve CrossAssetNavSearchBox experience for new searchUI (search pages), otherwise continue to use legacy experiences} */}
                {useNewSearchUI ? (
                  <CrossAssetNavSearchBox
                    contentType={contentType}
                    setSearchTerm={setSearchTerm}
                    onSubmit={handleSubmit}
                    searchTerm={searchTerm}
                    useNewSearchUI
                    placeholder={getPlaceholder(
                      prominentSearchOptions.find(
                        ({ id }) => id === contentType
                      )?.placeholderText
                    )}
                    suggestions={suggestions}
                    setSuggestions={setSuggestions}
                    clearSuggestions={clearSuggestions}
                  />
                ) : isMediumScreen ? (
                  <CrossAssetNavSearchBox
                    contentType={contentType}
                    setSearchTerm={setSearchTerm}
                    onSubmit={handleSubmit}
                    searchTerm={searchTerm}
                    suggestions={suggestions}
                    setSuggestions={setSuggestions}
                    clearSuggestions={clearSuggestions}
                    placeholder={getPlaceholder(
                      prominentSearchOptions.find(
                        ({ id }) => id === contentType
                      )?.placeholderText
                    )}
                  />
                ) : (
                  <NavSearchBox
                    contentType={contentType}
                    onChange={setSearchTerm}
                    onSubmit={handleSubmit}
                    searchTerm={searchTerm}
                    placeholder={getPlaceholder(
                      prominentSearchOptions.find(
                        ({ id }) => id === contentType
                      )?.placeholderText
                    )}
                    userHasAnySubscription={userHasAnySubscription}
                    isProminentSearch
                  />
                )}
                {showAiCta ? (
                  <>
                    {showDropDown && (
                      <ProminentSearchDropdown
                        onChange={handleDropdownChange}
                        value={contentType}
                        useNewSearchUI={useNewSearchUI}
                        useSnapshots={useSnapshots}
                        clearSuggestions={clearSuggestions}
                        variant="light"
                      />
                    )}
                    <RightArrowButton />
                  </>
                ) : (
                  <ProminentSearchButton />
                )}
              </form>
              <SearchWithAudio contentType={contentType} />
            </div>
          </div>
        )}
      </NavSearchContext.Consumer>
    </NavSearchContextProvider>
  );
}

ProminentSearch.defaultProps = {
  contentType: '',
  searchTerm: '',
};
